import { memo, useState } from 'react'
import { Document, Page } from 'react-pdf'
import { twMerge } from 'tailwind-merge'
import type { ChatAttachment, FinalizedAttachment, PendingAttachment } from '../../../../types'
import { formatBytes, getWordCountFormatted } from '../../../../utils/stringUtils'
import { FadingScrollable } from '../../../elements/FadingScrollable'
import { PreviewableContext } from '../../input/context/previewable-context'
import { AttachmentStub } from '../attachment-stub'
import { FileTypeIcon } from '../file-type-icon'
import { useRemoveAttachment } from '../hooks/use-remove-attachment'
import { scrollableAreaClassName, scrollablePreviewClassName } from '../styles'
import type { AttachmentPillProps } from '../types'
import { Image } from './image-attachment'

type AttachedFile =
  | Extract<FinalizedAttachment, { type: 'textFile' }>
  | Extract<FinalizedAttachment, { type: 'pdf' }>
  | Extract<FinalizedAttachment, { type: 'spreadsheet' }>

export const File = memo((attachment: AttachmentPillProps) => {
  const attachedFile = attachment.attachment as AttachedFile
  const pendingFile = attachment.pending as Extract<PendingAttachment, { type: 'file' }>
  const onRemove = useRemoveAttachment(attachment)

  if (pendingFile?.mimeType?.includes('image')) {
    return <Image {...attachment} />
  }

  const name =
    attachment.metadata?.label ??
    pendingFile?.name ??
    (attachedFile as Extract<FinalizedAttachment, { type: 'pdf' }>)?.fileName ??
    'File'

  return (
    <PreviewableContext preview={<FilePreview {...attachment} />}>
      <AttachmentStub label={name} icon={<FileTypeIcon name={pendingFile?.name ?? name} />} onRemove={onRemove} />
    </PreviewableContext>
  )
})
File.displayName = 'File'

const FilePreview = memo((attachment: ChatAttachment) => {
  const [loaded, setLoaded] = useState(false)

  const attachedFile = attachment.attachment as AttachedFile

  const plainTextFile = attachment.attachment as
    | Extract<FinalizedAttachment, { type: 'textFile' }>
    | Extract<FinalizedAttachment, { type: 'spreadsheet' }>

  const pendingFile = attachment.pending as Extract<PendingAttachment, { type: 'file' }>

  const isPdf = pendingFile?.mimeType?.includes('pdf')

  const isPlainText =
    typeof pendingFile?.body === 'string' || attachedFile?.type === 'textFile' || attachedFile?.type === 'spreadsheet'

  const hasPreview = isPlainText || isPdf || pendingFile?.body instanceof ArrayBuffer

  return (
    <div className={twMerge(scrollablePreviewClassName, 'p-0')}>
      {isPlainText && (
        <span className={'pl-4 pt-3.5 font-medium'}>
          {getWordCountFormatted((pendingFile?.body as string) ?? plainTextFile?.contents ?? '')} words
        </span>
      )}
      <FadingScrollable
        className={twMerge(
          scrollableAreaClassName,
          'rounded-2xl',
          !isPdf || !loaded ? 'p-4 pb-0 pt-3.5' : 'max-h-48',
          !hasPreview ? 'pb-3.5' : '',
        )}
        onClick={(e) => e.stopPropagation()}
        fadeSize="10%"
      >
        {isPlainText ? (
          ((pendingFile?.body as string) ?? plainTextFile?.contents)
        ) : isPdf ? (
          <Document file={pendingFile.body.slice(0)} onLoadSuccess={() => setLoaded(true)}>
            <Page
              pageNumber={1}
              height={192}
              onRenderAnnotationLayerError={() => {}}
              renderTextLayer={false}
              renderAnnotationLayer={false}
            />
          </Document>
        ) : pendingFile?.body instanceof ArrayBuffer ? (
          <div className={'flex flex-col gap-1'}>
            <span className={'font-medium'}>
              {pendingFile.name} {formatBytes(pendingFile.body.byteLength, 0)}
            </span>
            <span>File cannot be previewed</span>
          </div>
        ) : (
          (pendingFile?.name ?? attachedFile?.fileName ?? 'No preview available')
        )}
      </FadingScrollable>
    </div>
  )
})
FilePreview.displayName = 'FilePreview'
